/**
 * Browser notifications: the only channel that reaches someone with the tab
 * closed.
 *
 * Three separate things have to line up before one can be sent, and they fail
 * in different ways, so each is reported separately rather than as one
 * boolean:
 *
 * - the **browser** has to support service workers and push (Safari only has
 *   since 16.4, and on iOS only for an installed home-screen app),
 * - the **deployment** has to have a VAPID keypair (`available` from
 *   `/push/public-key`); without one there is nothing to subscribe to,
 * - the **person** has to grant permission, which can only be asked from a
 *   real click and, once denied, cannot be asked again from script.
 *
 * `enablePush` does the whole dance and returns which of those stopped it, so
 * the UI can say something true instead of "failed".
 */

import apiClient from './api'

/** Where the worker lives. At the site root on purpose: a service worker can
 *  only control pages at or below its own path, and this has to work on every
 *  screen. */
const SERVICE_WORKER_PATH = '/push-sw.js'

export type PushState =
  | 'unsupported' // this browser cannot
  | 'unavailable' // this deployment has no keypair
  | 'denied' // the person said no, and cannot be asked again from script
  | 'default' // never asked
  | 'enabled'

export interface PushStatus {
  state: PushState
  /** How many browsers this user currently gets notifications on. */
  subscriptions: number
}

export const pushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

/** The base64url the browser's subscribe() wants, as the bytes it wants. */
const toUint8Array = (base64Url: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}

/** The two keys, out of the browser's own subscription object, as base64url. */
const keyOf = (subscription: PushSubscription, name: 'p256dh' | 'auth'): string => {
  const key = subscription.getKey(name)
  if (!key) return ''
  let binary = ''
  const bytes = new Uint8Array(key)
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i])
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

interface Availability {
  available: boolean
  public_key?: string | null
  subscriptions: number
}

const availability = async (): Promise<Availability> => {
  const res = await apiClient.get('/push/public-key')
  return {
    available: !!res.data?.available,
    public_key: res.data?.public_key ?? null,
    subscriptions: res.data?.subscriptions ?? 0,
  }
}

/** Where things stand, without asking the person anything. */
export const getPushStatus = async (): Promise<PushStatus> => {
  if (!pushSupported()) return { state: 'unsupported', subscriptions: 0 }
  let server: Availability
  try {
    server = await availability()
  } catch {
    return { state: 'unavailable', subscriptions: 0 }
  }
  if (!server.available) return { state: 'unavailable', subscriptions: 0 }
  if (Notification.permission === 'denied') {
    return { state: 'denied', subscriptions: server.subscriptions }
  }
  if (Notification.permission === 'granted' && server.subscriptions > 0) {
    return { state: 'enabled', subscriptions: server.subscriptions }
  }
  return { state: 'default', subscriptions: server.subscriptions }
}

/**
 * Ask, subscribe and register — the whole dance.
 *
 * Must be called from a real click: `Notification.requestPermission()` is
 * ignored otherwise, and on Safari it rejects.
 */
export const enablePush = async (): Promise<PushStatus> => {
  if (!pushSupported()) return { state: 'unsupported', subscriptions: 0 }

  const server = await availability().catch(() => null)
  if (!server || !server.available || !server.public_key) {
    return { state: 'unavailable', subscriptions: 0 }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return {
      state: permission === 'denied' ? 'denied' : 'default',
      subscriptions: server.subscriptions,
    }
  }

  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
  // `ready` rather than the registration's own promise: a worker that is
  // installed but not yet active cannot be subscribed through.
  await navigator.serviceWorker.ready

  // An existing subscription may have been made against an older keypair, in
  // which case the push service will reject everything sent to it. Replacing
  // it is cheaper than detecting that later.
  const existing = await registration.pushManager.getSubscription()
  if (existing) await existing.unsubscribe().catch(() => undefined)

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: toUint8Array(server.public_key),
  })

  const res = await apiClient.post('/push/subscriptions', {
    endpoint: subscription.endpoint,
    p256dh: keyOf(subscription, 'p256dh'),
    auth: keyOf(subscription, 'auth'),
  })
  return { state: 'enabled', subscriptions: res.data?.subscriptions ?? 1 }
}

/** Stop notifying this browser, on both sides. */
export const disablePush = async (): Promise<PushStatus> => {
  if (!pushSupported()) return { state: 'unsupported', subscriptions: 0 }
  try {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
    const subscription = await registration?.pushManager.getSubscription()
    if (subscription) {
      await apiClient
        .post('/push/subscriptions/delete', { endpoint: subscription.endpoint })
        .catch(() => undefined)
      await subscription.unsubscribe().catch(() => undefined)
    }
  } catch {
    /* the server side is what decides whether anything is sent */
  }
  return await getPushStatus()
}

/** Send one to this user now — a notification you cannot try is one you do
 *  not know is broken until the morning you needed it. */
export const sendTestPush = async (): Promise<string> => {
  const res = await apiClient.post('/push/test')
  return res.data?.message || '送信しました。'
}
